import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './EducationProgramHeroForm.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function StudentLifeEventsForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  // Form values (auto-filled from content_published)
  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    events: []
  });

  // Pending approvals per field (kicker, title, description)
  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false
  });

  // Pending approvals per event (event_0, event_1, etc)
  const [pendingEvents, setPendingEvents] = useState({});

  // Track original values to detect changes
  const [originalData, setOriginalData] = useState({...formData});

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [editingEventIndex, setEditingEventIndex] = useState(null);
  const [deletingEventIndex, setDeletingEventIndex] = useState(null);
  const [mediaMap, setMediaMap] = useState({});

  useEffect(() => {
    // Load media map and section data in parallel
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      console.log('[StudentLifeEventsForm] Fetching media map...');
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        }
      );
      
      if (!response.ok) {
        console.warn('[StudentLifeEventsForm] Media fetch failed:', response.status);
        return;
      }
      
      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            map[parseInt(mediaId)] = mediaUrl;
            console.log(`[StudentLifeEventsForm] ✓ Media ${mediaId} → ${mediaUrl.substring(0, 60)}...`);
          }
        });
        
        console.log('[StudentLifeEventsForm] ✓ Media map ready:', Object.keys(map).length, 'items');
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeEventsForm] Media fetch error:', err.message);
    }
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[StudentLifeEventsForm] Loading student life events section...');
      
      // Approach 1: Try admin endpoint (requires auth)
      console.log('[StudentLifeEventsForm] Trying authenticated endpoint...');
      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: { 
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache'
          }
        }
      );
      
      let eventsSection = null;
      
      if (adminResponse.ok) {
        const result = await adminResponse.json();
        
        if (result.ok && Array.isArray(result.data)) {
          eventsSection = result.data.find(s => s.section_type === 'student-life-events');
          console.log('[StudentLifeEventsForm] ✓ Got data from admin endpoint');
        }
      } else if (adminResponse.status === 401) {
        console.log('[StudentLifeEventsForm] Admin endpoint unauthorized, trying public endpoint...');
      }
      
      // Approach 2: Fallback to public endpoint
      if (!eventsSection) {
        const publicResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          { 
            credentials: 'include',
            headers: { 
              'Content-Type': 'application/json',
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              'Pragma': 'no-cache'
            }
          }
        );
        
        if (publicResponse.ok) {
          const result = await publicResponse.json();
          
          if (result.ok && Array.isArray(result.data)) {
            eventsSection = result.data.find(s => s.section_type === 'student-life-events');
            console.log('[StudentLifeEventsForm] ✓ Got data from public endpoint');
          }
        } else {
          throw new Error(`Public endpoint returned ${publicResponse.status}`);
        }
      }
      
      if (!eventsSection) {
        throw new Error('Events section not found');
      }

      const sectionId = eventsSection.id;
      setSectionId(sectionId);

      console.log('[StudentLifeEventsForm] Found events section:', sectionId);

      // Load section with pending changes (if authenticated)
      let pendingFieldsMap = { kicker: false, title: false, description: false };
      let pendingEventsMap = {};
      
      const pendingResponse = await fetch(
        `${apiBase}/cms/section/${sectionId}/with-pending`,
        { 
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (pendingResponse.ok) {
        try {
          const pendingResult = await pendingResponse.json();
          
          if (pendingResult.success && pendingResult.data && pendingResult.data.pending_changes) {
            const { pending_changes } = pendingResult.data;

            if (Array.isArray(pending_changes)) {
              pending_changes.forEach(change => {
                if (change.status === 'pending' && change.locale === 'en') {
                  if (change.field_id === 'kicker' || change.field_id === 'title' || change.field_id === 'description') {
                    pendingFieldsMap[change.field_id] = true;
                  } else if (change.field_id && change.field_id.includes('events.')) {
                    const match = change.field_id.match(/events\.(\d+)\./);
                    if (match) {
                      const eventIdx = parseInt(match[1]);
                      pendingEventsMap[`event_${eventIdx}`] = true;
                    }
                  }
                }
              });
            }
          }
        } catch (e) {
          console.warn('[StudentLifeEventsForm] Error parsing pending changes:', e);
        }
      }

      // Extract published content
      if (eventsSection.content_published) {
        let published = eventsSection.content_published;
        
        if (typeof published === 'string') {
          try {
            published = JSON.parse(published);
          } catch (e) {
            published = {};
          }
        }

        const publishedEn = published.en || {};
        
        const initialData = {
          kicker: publishedEn.kicker || '',
          title: publishedEn.title || '',
          description: publishedEn.description || '',
          events: Array.isArray(publishedEn.events) ? publishedEn.events : []
        };

        console.log(`[StudentLifeEventsForm] Initial data loaded:`, {
          kicker: initialData.kicker,
          title: initialData.title,
          description: initialData.description?.substring(0, 80),
          eventsCount: initialData.events.length
        });

        setFormData(initialData);
        setOriginalData(initialData);
        setPendingFields(pendingFieldsMap);
        setPendingEvents(pendingEventsMap);

        console.log('[StudentLifeEventsForm] ✓ Section loaded:', {
          id: sectionId,
          eventsCount: initialData.events.length,
          pendingFields: pendingFieldsMap,
          pendingEvents: pendingEventsMap
        });
      }
    } catch (err) {
      console.error('[StudentLifeEventsForm] Load error:', err);
      toast.error('Error loading form: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleEventChange = (index, field, value) => {
    setFormData(prev => {
      const newEvents = [...prev.events];
      if (typeof newEvents[index] !== 'object' || newEvents[index] === null) {
        newEvents[index] = {};
      }
      newEvents[index] = {
        ...newEvents[index],
        [field]: value
      };
      return {
        ...prev,
        events: newEvents
      };
    });
  };

  const handleImageUpload = async (eventIndex, imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error(`Max 5MB, got ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP allowed');
      return;
    }

    const key = `${eventIndex}-${imgIndex}`;
    setImageUploading(prev => ({ ...prev, [key]: true }));
    setImageProgress(prev => ({ ...prev, [key]: 0 }));

    try {
      const formDataToUpload = new FormData();
      formDataToUpload.append('file', file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          console.log('[StudentLifeEventsForm] Upload progress:', percent + '%');
          setImageProgress(prev => ({ ...prev, [key]: percent }));
        }
      });

      xhr.addEventListener('load', () => {
        console.log('[StudentLifeEventsForm] Upload completed, status:', xhr.status);
        console.log('[StudentLifeEventsForm] Response text:', xhr.responseText);
        
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);
            console.log('[StudentLifeEventsForm] Parsed response:', response);
            
            if (response.success || response.ok) {
              const mediaId = response.media_id || response.id || response.filename;
              console.log('[StudentLifeEventsForm] ✓ Image uploaded, media ID:', mediaId);
              
              if (mediaId) {
                const currentEvent = formData.events[eventIndex];
                const currentImages = Array.isArray(currentEvent?.images) ? [...currentEvent.images] : [];
                
                // Ensure array has enough slots
                while (currentImages.length <= imgIndex) {
                  currentImages.push(null);
                }
                
                // Update the specific image slot with media ID
                currentImages[imgIndex] = mediaId;
                
                // Remove null values and update
                const filteredImages = currentImages.filter(Boolean);
                handleEventChange(eventIndex, 'images', filteredImages);
                
                // Update mediaMap locally so preview shows immediately
                setMediaMap(prev => ({
                  ...prev,
                  [mediaId]: response.url || response.file_path || `/api/uploads/studentlife/${response.filename}`
                }));
                
                console.log('[StudentLifeEventsForm] Images array updated:', filteredImages);
                toast.success('✓ Image uploaded (ID: ' + mediaId + ')');
              } else {
                toast.error('No media ID in response');
              }
            } else {
              toast.error(response.error || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[StudentLifeEventsForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          console.error('[StudentLifeEventsForm] Upload failed with status', xhr.status);
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.addEventListener('error', () => {
        console.error('[StudentLifeEventsForm] XHR error event');
        toast.error('Upload failed - network error');
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      console.log('[StudentLifeEventsForm] Opening XHR to:', `${apiBase}/studentlife/cms/media/upload`);
      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      xhr.send(formDataToUpload);
    } catch (err) {
      console.error('[StudentLifeEventsForm] Upload error:', err);
      toast.error('Upload error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    // Check if value changed
    if (JSON.stringify(formData[fieldName]) === JSON.stringify(originalData[fieldName])) {
      toast.warning('No changes to submit');
      return;
    }

    console.log(`[StudentLifeEventsForm] Submitting field: ${fieldName}`, {
      old: originalData[fieldName],
      new: formData[fieldName],
      sectionId
    });

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const requestBody = {
        section_id: sectionId,
        field_id: fieldName,
        field_name: fieldName.replace(/_/g, ' '),
        new_value: formData[fieldName],
        old_value: originalData[fieldName],
        locale: 'en'
      };
      
      console.log(`[StudentLifeEventsForm] Sending request:`, JSON.stringify(requestBody));

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(requestBody)
        }
      );

      const result = await response.json();
      console.log(`[StudentLifeEventsForm] Response from submit:`, result);

      if (result.success) {
        toast.success(`Submitted for approval`);
        
        // Mark as pending locally
        setPendingFields(prev => ({
          ...prev,
          [fieldName]: true
        }));

        // Update original data
        setOriginalData(prev => ({
          ...prev,
          [fieldName]: formData[fieldName]
        }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitEvent = async (eventIndex, operation = null) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const event = formData.events[eventIndex];
    if (!event) {
      toast.error('Event not found');
      return;
    }

    // Validate event has title (not needed for delete)
    if (operation !== 'delete' && (!event.title || event.title.trim() === '')) {
      toast.error('Event title is required');
      return;
    }

    // Auto-detect operation if not provided
    if (!operation) {
      // isNewEvent: event exists in formData but NOT in originalData
      const isNewEvent = !originalData.events[eventIndex];
      operation = isNewEvent ? 'add' : 'update';
    }

    const eventKey = `event_${eventIndex}`;
    setSubmitting(prev => ({ ...prev, [eventKey]: true }));

    try {
      // Ensure images are array of IDs (strings or numbers)
      const eventToSubmit = operation === 'delete' ? null : {
        ...event,
        highlights: Array.isArray(event.highlights) ? event.highlights : [],
        schedule: Array.isArray(event.schedule) ? event.schedule : [],
        images: Array.isArray(event.images) ? event.images.filter(Boolean) : []
      };

      const fieldId = `events.${eventIndex}.${operation}`;
      const fieldName = operation === 'add' ? `Add Event: ${event.title}` :
                        operation === 'update' ? `Update Event: ${event.title}` :
                        `Delete Event: ${event.title}`;

      console.log('[StudentLifeEventsForm] Submitting event operation:', {
        eventIndex, operation, fieldId,
        isNew: !originalData.events[eventIndex],
        sectionId
      });

      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldId,
            field_name: fieldName,
            old_value: originalData.events[eventIndex] || null,
            new_value: eventToSubmit,
            locale: 'en',
            operation_type: operation
          })
        }
      );

      const result = await response.json();
      console.log('[StudentLifeEventsForm] Submit response:', result);

      if (result.success) {
        const opLabel = operation === 'add' ? 'added' : operation === 'update' ? 'updated' : 'deleted';
        toast.success(`Event ${opLabel} and submitted for approval`);

        if (operation === 'add') {
          // Mark pending for this new event index
          setPendingEvents(prev => ({ ...prev, [eventKey]: true }));
          // Update originalData to include this new event so subsequent edits use 'update'
          setOriginalData(prev => ({
            ...prev,
            events: [...prev.events, eventToSubmit]
          }));
        } else if (operation === 'update') {
          setPendingEvents(prev => ({ ...prev, [eventKey]: true }));
          setOriginalData(prev => ({
            ...prev,
            events: prev.events.map((e, i) => i === eventIndex ? eventToSubmit : e)
          }));
        } else if (operation === 'delete') {
          // Remove from both formData and originalData locally
          setFormData(prev => ({
            ...prev,
            events: prev.events.filter((_, i) => i !== eventIndex)
          }));
          setOriginalData(prev => ({
            ...prev,
            events: prev.events.filter((_, i) => i !== eventIndex)
          }));
          setPendingEvents(prev => {
            const next = { ...prev };
            delete next[eventKey];
            return next;
          });
        }

        setEditingEventIndex(null);
        setDeletingEventIndex(null);
      } else {
        if (response.status === 409) {
          toast.error(`Event already has a pending approval`);
        } else {
          toast.error(result.error || result.message || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[StudentLifeEventsForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [eventKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="education-hero-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading Events & Competitions Section...
        </div>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Events & Competitions</h2>
          <p className="hero-form-description">Manage events section header, content, and individual event details</p>
        </div>

        {/* KICKER SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Kicker</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Kicker *</label>
              {pendingFields.kicker && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="100"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={submitting.kicker || pendingFields.kicker}
              placeholder="e.g., Events & Competitions"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              className="submit-btn"
            >
              {submitting.kicker ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* TITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Title</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Title *</label>
              {pendingFields.title && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              maxLength="200"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={submitting.title || pendingFields.title}
              placeholder="e.g., SPARTAN Cups, Entrepreneurship, and STEM highlights"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              className="submit-btn"
            >
              {submitting.title ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* DESCRIPTION SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Description</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Description *</label>
              {pendingFields.description && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <textarea
              maxLength="500"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={submitting.description || pendingFields.description}
              placeholder="Brief description of events and competitions program..."
              className="textarea-input"
              rows="4"
            />
            <button
              onClick={() => handleSubmitField('description')}
              disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              className="submit-btn"
            >
              {submitting.description ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* EVENTS SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Events ({formData.events.length})</h3>

          <div className="cards-grid">
            {formData.events.length === 0 ? (
              <div className="items-empty-state" style={{ gridColumn: '1 / -1' }}>
                <div className="empty-icon">🎯</div>
                <h4>No events configured</h4>
                <p>Click "Add New Event" to create your first event</p>
              </div>
            ) : (
              formData.events.map((event, idx) => {
                let eventImage = '';
                if (typeof event === 'object' && event !== null && event.images) {
                  eventImage = event.images[0] || '';
                }
                
                return (
                  <div
                    key={idx}
                    className="slide-card"
                    onClick={() => setEditingEventIndex(idx)}
                  >
                    {eventImage && mediaMap[eventImage] && (
                      <div className="card-image">
                        <img src={mediaMap[eventImage]} alt={event.title || `Event ${idx + 1}`} />
                      </div>
                    )}
                    <div className="card-content">
                      <div className="card-order">Event {idx + 1}</div>
                      <h4 className="card-title">{event.title || 'Event'}</h4>
                      <p className="card-desc">{event.subtitle || 'Click to edit'}</p>
                    </div>
                    {pendingEvents[`event_${idx}`] && (
                      <div className="card-pending">⏳</div>
                    )}
                  </div>
                );
              })
            )}

            {/* Add New Event Card */}
            <div
              className="slide-card add-card"
              onClick={() => {
                const newEvent = {
                  title: '',
                  subtitle: '',
                  description: '',
                  images: [],
                  highlights: [],
                  schedule: []
                };
                setFormData(prev => {
                  const updated = {
                    ...prev,
                    events: [...prev.events, newEvent]
                  };
                  // Set editing to new event after state updates
                  setTimeout(() => setEditingEventIndex(updated.events.length - 1), 0);
                  return updated;
                });
              }}
              style={{ cursor: 'pointer', opacity: 0.7 }}
            >
              <div className="card-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px' }}>➕</div>
                  <h4 style={{ margin: 0, fontSize: '16px' }}>Add New Event</h4>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* EDIT EVENT MODAL */}
        {editingEventIndex !== null && formData.events[editingEventIndex] && (
          <div className="modal-overlay" onClick={() => setEditingEventIndex(null)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Edit Event: {formData.events[editingEventIndex].title || `Event ${editingEventIndex + 1}`}</h3>
                <button className="modal-close" onClick={() => setEditingEventIndex(null)}>×</button>
              </div>

              <div className="modal-body">
                <div className="modal-field">
                  <label>Event Title</label>
                  <input
                    type="text"
                    value={formData.events[editingEventIndex].title || ''}
                    onChange={(e) => handleEventChange(editingEventIndex, 'title', e.target.value)}
                    placeholder="e.g., SPARTAN CUPS"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Event Subtitle</label>
                  <input
                    type="text"
                    value={formData.events[editingEventIndex].subtitle || ''}
                    onChange={(e) => handleEventChange(editingEventIndex, 'subtitle', e.target.value)}
                    placeholder="e.g., Annual sports, arts, and leadership competitions"
                    className="text-input"
                  />
                </div>

                <div className="modal-field">
                  <label>Description</label>
                  <textarea
                    value={formData.events[editingEventIndex].description || ''}
                    onChange={(e) => handleEventChange(editingEventIndex, 'description', e.target.value)}
                    placeholder="Detailed description of the event..."
                    className="textarea-input"
                    rows="3"
                  />
                </div>

                {/* HIGHLIGHTS */}
                <div className="modal-field">
                  <label>Highlights (Key Points)</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const highlights = formData.events[editingEventIndex]?.highlights;
                      const highlightsArray = Array.isArray(highlights) ? [...highlights] : [];
                      // Ensure at least 3 highlight slots
                      while (highlightsArray.length < 3) {
                        highlightsArray.push('');
                      }
                      return highlightsArray.slice(0, 5).map((highlight, hIdx) => (
                        <div key={hIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={highlight || ''}
                            onChange={(e) => {
                              const updated = [...highlightsArray];
                              updated[hIdx] = e.target.value;
                              handleEventChange(editingEventIndex, 'highlights', updated.filter(Boolean));
                            }}
                            placeholder={`Highlight ${hIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          {hIdx >= 3 && (
                            <button
                              onClick={() => {
                                const updated = highlightsArray.filter((_, i) => i !== hIdx);
                                handleEventChange(editingEventIndex, 'highlights', updated.filter(Boolean));
                              }}
                              style={{ padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const updated = [...(formData.events[editingEventIndex]?.highlights || [])];
                        updated.push('');
                        handleEventChange(editingEventIndex, 'highlights', updated);
                      }}
                      style={{ marginTop: '8px', padding: '8px 12px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      + Add Highlight
                    </button>
                  </div>
                </div>

                {/* SCHEDULE */}
                <div className="modal-field">
                  <label>Schedule (Timeline)</label>
                  <div style={{ marginBottom: '12px' }}>
                    {(() => {
                      const schedule = formData.events[editingEventIndex]?.schedule;
                      const scheduleArray = Array.isArray(schedule) ? [...schedule] : [];
                      // Ensure at least 3 schedule slots
                      while (scheduleArray.length < 3) {
                        scheduleArray.push('');
                      }
                      return scheduleArray.slice(0, 5).map((phase, pIdx) => (
                        <div key={pIdx} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            type="text"
                            value={phase || ''}
                            onChange={(e) => {
                              const updated = [...scheduleArray];
                              updated[pIdx] = e.target.value;
                              handleEventChange(editingEventIndex, 'schedule', updated.filter(Boolean));
                            }}
                            placeholder={`Phase/Week ${pIdx + 1}`}
                            className="text-input"
                            style={{ flex: 1 }}
                          />
                          {pIdx >= 3 && (
                            <button
                              onClick={() => {
                                const updated = scheduleArray.filter((_, i) => i !== pIdx);
                                handleEventChange(editingEventIndex, 'schedule', updated.filter(Boolean));
                              }}
                              style={{ padding: '8px 12px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: '4px', cursor: 'pointer' }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ));
                    })()}
                    <button
                      onClick={() => {
                        const updated = [...(formData.events[editingEventIndex]?.schedule || [])];
                        updated.push('');
                        handleEventChange(editingEventIndex, 'schedule', updated);
                      }}
                      style={{ marginTop: '8px', padding: '8px 12px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                    >
                      + Add Phase
                    </button>
                  </div>
                </div>

                {/* IMAGES */}
                <div className="modal-field">
                  <label>Event Images</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '12px' }}>
                    {[0, 1].map((imgIndex) => {
                      const img = formData.events[editingEventIndex]?.images?.[imgIndex];
                      const imgUrl = img ? mediaMap[img] : null;
                      return (
                        <div key={imgIndex} style={{ border: '1px dashed #ccc', borderRadius: '8px', overflow: 'hidden' }}>
                          {imgUrl && (
                            <img
                              src={imgUrl}
                              alt={`Event ${editingEventIndex + 1} - Image ${imgIndex + 1}`}
                              style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                            />
                          )}
                          <div style={{ padding: '12px', background: '#f9fafb' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                              Image {imgIndex + 1} {img ? `(ID: ${img})` : '(empty)'}
                            </div>
                            <label style={{ display: 'block', cursor: 'pointer', padding: '8px', background: '#e0f2fe', border: '1px solid #0284c7', borderRadius: '4px', textAlign: 'center', fontSize: '12px', fontWeight: '500' }}>
                              {imageUploading[`${editingEventIndex}-${imgIndex}`] 
                                ? `Uploading ${imageProgress[`${editingEventIndex}-${imgIndex}`] || 0}%`
                                : 'Upload Image'
                              }
                              <input
                                type="file"
                                onChange={(e) => handleImageUpload(editingEventIndex, imgIndex, e)}
                                disabled={imageUploading[`${editingEventIndex}-${imgIndex}`]}
                                accept="image/*"
                                style={{ display: 'none' }}
                              />
                            </label>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* ACTION BUTTONS */}
                <div style={{ display: 'flex', gap: '12px', marginTop: '24px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                  <button
                    onClick={() => handleSubmitEvent(editingEventIndex)}
                    disabled={submitting[`event_${editingEventIndex}`]}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      background: '#16a34a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {submitting[`event_${editingEventIndex}`] ? 'Submitting...' : 'Submit for Approval'}
                  </button>
                  {originalData.events[editingEventIndex] && (
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this event?')) {
                          handleSubmitEvent(editingEventIndex, 'delete');
                        }
                      }}
                      disabled={submitting[`event_${editingEventIndex}`]}
                      style={{
                        padding: '10px 16px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Delete
                    </button>
                  )}
                  <button
                    onClick={() => setEditingEventIndex(null)}
                    style={{
                      padding: '10px 16px',
                      background: '#e5e7eb',
                      color: '#374151',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
